<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="600px"
    persistent
    @keydown.esc="$emit('close')"
  >
    <v-card>
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-package-variant-plus</v-icon>
        {{ $t('stock.title.adjust_stock') }}
      </v-card-title>

      <v-card-subtitle class="pb-0">
        {{ $t('stock.title.adjust_stock_description') }}
      </v-card-subtitle>

      <v-divider class="mt-2"></v-divider>

      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-card outlined class="mb-4 pa-3">
                  <div class="d-flex align-center">
                    <v-avatar color="primary" size="36" class="mr-3">
                      <v-icon dark>mdi-package</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-h6">{{ item.name }}</div>
                      <div class="text-caption">{{ item.code }}</div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6">
                <v-menu
                  ref="dateMenu"
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedDate"
                      :label="$t('label.date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :rules="rules.date"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="data.date"
                    @input="dateMenu = false"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="data.warehouse"
                  :items="warehouseItems"
                  :label="$t('stock.label.warehouse')"
                  item-text="name"
                  item-value="id"
                  prepend-icon="mdi-warehouse"
                  outlined
                  dense
                  :rules="rules.warehouse"
                  :disabled="!add && !warehouseHasItems"
                >
                  <template v-slot:item="{ item }">
                    {{ item.name }} ({{ item.quantity }})
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.name }} ({{ item.quantity }})
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12">
                <v-radio-group
                  v-model="data.type"
                  row
                  :rules="rules.type"
                >
                  <template v-slot:label>
                    <div class="mb-1">{{ $t('stock.label.type') }}</div>
                  </template>
                  <v-radio
                    label="Increase Quantity"
                    value="inc"
                    color="success"
                  >
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="success" left>mdi-plus-circle</v-icon>
                        {{ $t('stock.label.increase_quantity') }}
                      </div>
                    </template>
                  </v-radio>
                  <v-radio
                    label="Decrease Quantity"
                    value="dec"
                    color="error"
                  >
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="error" left>mdi-minus-circle</v-icon>
                        {{ $t('stock.label.decrease_quantity') }}
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-col>

              <v-col cols="12" sm="6" v-show="add">
                <v-text-field
                  v-model.number="data.cost_price"
                  :label="$t('stock.label.cost_price')"
                  type="number"
                  outlined
                  dense
                  prepend-icon="mdi-currency-usd"
                  :rules="add ? rules.cost_price : []"
                ></v-text-field>
              </v-col>

              <v-col cols="12" :sm="add ? 6 : 12">
                <v-text-field
                  v-model.number="data.quantity"
                  :label="add ? $t('stock.label.increase_by') : $t('stock.label.decrease_by')"
                  type="number"
                  outlined
                  dense
                  :disabled="!adjustable"
                  :prepend-icon="add ? 'mdi-plus' : 'mdi-minus'"
                  :rules="rules.quantity"
                  :color="add ? 'success' : 'error'"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  :value="totalStock"
                  :label="$t('stock.label.total_stock')"
                  outlined
                  dense
                  readonly
                  prepend-icon="mdi-package-variant-closed"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  :value="newQuantity"
                  label="New Quantity"
                  outlined
                  dense
                  readonly
                  :color="newQuantity >= 0 ? 'success' : 'error'"
                  :prepend-icon="newQuantity >= 0 ? 'mdi-package-variant' : 'mdi-alert'"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="data.reason"
                  :items="reasonItems"
                  :label="$t('stock.label.reason')"
                  outlined
                  dense
                  prepend-icon="mdi-information-outline"
                  :rules="rules.reason"
                  clearable
                  auto-select-first
                  cache-items
                  :filter="customFilter"
                  small-chips
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="$emit('close')"
          :disabled="loading"
        >
          <v-icon left>mdi-close</v-icon>
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!adjustable || !valid"
          :loading="loading"
          @click="saveStockAdjustment"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ $t('button.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AdjustStock',
  props: {
    display: { type: Boolean },
    item: { type: Object, required: true },
    totalStock: { type: Number, required: false, default: 0 }
  },
  data () {
    return {
      dialogVisible: false,
      valid: true,
      loading: false,
      dateMenu: false,
      data: {
        cost_price: null,
        type: 'inc',
        date: new Date().toISOString().substr(0, 10),
        quantity: null,
        reason: '',
        warehouse: null
      },
      reasons: [
        { key: 1, value: this.$t('stock.reasons.take_adjustment') },
        { key: 2, value: this.$t('stock.reasons.written_off') },
        { key: 3, value: this.$t('stock.reasons.damaged') },
        { key: 4, value: this.$t('stock.reasons.complement') }
      ],
      warehouses: [],
      rules: {
        reason: [
          v => !!v || this.$t('validation.required') || 'Reason is required'
        ],
        quantity: [
          v => !!v || this.$t('validation.required') || 'Quantity is required',
          v => (v && v > 0) || 'Quantity must be greater than 0'
        ],
        date: [
          v => !!v || this.$t('validation.required') || 'Date is required'
        ],
        warehouse: [
          v => !!v || this.$t('validation.required') || 'Warehouse is required'
        ],
        cost_price: [
          v => !this.add || !!v || this.$t('validation.required') || 'Cost price is required',
          v => !this.add || (v && v >= 0) || 'Cost price must be a positive number'
        ],
        type: [
          v => !!v || this.$t('validation.required') || 'Type is required'
        ]
      }
    }
  },
  computed: {
    add () {
      return this.data.type === 'inc'
    },
    adjustable () {
      return this.add || (!this.add && this.totalStock > 0)
    },
    formattedDate () {
      if (!this.data.date) return ''
      const [year, month, day] = this.data.date.split('-')
      return `${month}/${day}/${year}`
    },
    warehouseItems () {
      return this.warehouses.map(wh => ({
        id: wh.id,
        name: wh.name,
        quantity: wh.items.length ? wh.items[0].pivot.quantity : 0,
        disabled: !this.add && (!wh.items.length || !wh.items[0].pivot.quantity)
      }))
    },
    reasonItems () {
      return this.reasons.map(reason => reason.value)
    },
    newQuantity () {
      if (!this.data.quantity) return this.totalStock
      return this.add
        ? (Number(this.data.quantity) + Number(this.totalStock))
        : (Number(this.totalStock) - Number(this.data.quantity))
    },
    warehouseHasItems () {
      if (!this.data.warehouse) return false
      const warehouse = this.warehouses.find(wh => wh.id === this.data.warehouse)
      return warehouse && warehouse.items && warehouse.items.length && warehouse.items[0].pivot.quantity > 0
    }
  },
  watch: {
    display (val) {
      this.dialogVisible = val
      if (val) {
        this.openedHandler()
      }
    },
    dialogVisible (val) {
      if (!val) {
        this.$emit('close')
      }
    },
    'data.quantity' (value) {
      let newValue = value

      // Apply validations to determine the correct value
      if (value < 0) newValue = 0
      else if (!this.add && value > this.totalStock) {
        newValue = this.totalStock
      } else if (!this.add && this.data.warehouse) {
        const warehouse = this.warehouses.find(wh => wh.id === this.data.warehouse)
        if (warehouse && warehouse.items && warehouse.items.length && value > warehouse.items[0].pivot.quantity) {
          newValue = warehouse.items[0].pivot.quantity
        }
      }

      // Only update if the value has changed to prevent infinite loop
      if (newValue !== value) {
        this.$nextTick(() => {
          this.data.quantity = newValue
        })
      }
    },
    'data.type' (value) {
      // Handle warehouse selection when type changes to decrease
      if (this.data.warehouse && !this.add) {
        const warehouse = this.warehouses.find(wh => wh.id === this.data.warehouse)
        const items = warehouse ? warehouse.items : []

        if (!items.length || !items[0].pivot.quantity) {
          this.data.warehouse = null
        }
      }

      // Handle quantity constraints
      if (this.data.quantity) {
        let newQuantity = this.data.quantity

        if (!this.add && newQuantity > this.totalStock) {
          newQuantity = this.totalStock
        }

        if (this.data.warehouse && !this.add) {
          const warehouse = this.warehouses.find(wh => wh.id === this.data.warehouse)
          const items = warehouse ? warehouse.items : []

          if (items.length && items[0].pivot.quantity < newQuantity) {
            newQuantity = items[0].pivot.quantity
          }
        }

        // Only update if the value has changed
        if (newQuantity !== this.data.quantity) {
          this.$nextTick(() => {
            this.data.quantity = newQuantity
          })
        }
      }
    }
  },
  methods: {
    customFilter (item, queryText) {
      return item.toLowerCase().indexOf(queryText.toLowerCase()) > -1
    },
    async saveStockAdjustment () {
      if (!this.$refs.form.validate()) {
        return
      }

      this.askQuestion(this.$t('stock.confirm.adjust'), async () => {
        this.loading = true
        try {
          await this.withLoading(async () => {
            await this.$warehouseService.adjustStockItem(this.item.id, this.data)
            this.showSuccess(this.$t('stock.message.adjusted'))
            this.$emit('adjusted')
            this.$emit('close')
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.log('Component caught error in adjustStock:', error)
        } finally {
          this.loading = false
        }
      })
    },
    openedHandler () {
      this.data.cost_price = this.item.unit_price
      this.data.date = new Date().toISOString().substr(0, 10)
      this.$warehouseService.fetchWarehousesItem(this.item.id)
        .then(whs => {
          this.warehouses = whs
        })
    },
    resetForm () {
      this.data = {
        cost_price: this.item.unit_price,
        type: 'inc',
        date: new Date().toISOString().substr(0, 10),
        quantity: null,
        reason: '',
        warehouse: null
      }
      this.$refs.form && this.$refs.form.resetValidation()
    }
  }
}
</script>

<style scoped>
/* Ensure dialog content is properly spaced */
.v-card__text {
  padding-top: 16px !important;
}

/* Improve form field spacing */
.v-text-field {
  margin-top: 8px !important;
}

/* Make radio buttons more prominent */
.v-radio {
  margin-right: 12px;
}

/* Ensure consistent spacing in the form */
.v-form {
  margin-bottom: 8px;
}

/* Add subtle hover effect to the card */
.v-card.outlined {
  transition: all 0.3s ease;
}
.v-card.outlined:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Ensure icons are properly aligned */
.v-icon.v-icon {
  vertical-align: middle;
}

/* Improve button spacing */
.v-btn {
  margin-left: 8px;
}
</style>
